import { useEffect, useMemo, useState } from "react";
import Dropdown from "@/components/Dropdown";
import Checkbox from "@/components/Checkbox";
import Search from "@/components/Search";
import { SearchVariant, Size } from "@ever-tutor/et-ui";

export type Option<T> = { value: T; label: string };

interface MultiSelectDropdownProps<T> {
  label: string;
  options: Option<T>[];
  selected: Set<T>;
  onToggleValue: (value: T) => void;
  // Called with the currently visible/filtered values, not all options, so
  // select-all under a search query only touches what's shown.
  onClear: (values: T[]) => void;
  onSelectAll: (values: T[]) => void;
  isOpen: boolean;
  onToggle: () => void;
  fullWidth?: boolean;
  // Presence turns the search box on; the other filters have short, fixed
  // option lists and don't pass it.
  searchPlaceholder?: string;
}

function MultiSelectDropdown<T extends string | number>({
  label,
  options,
  selected,
  onToggleValue,
  onClear,
  onSelectAll,
  isOpen,
  onToggle,
  fullWidth = false,
  searchPlaceholder,
}: MultiSelectDropdownProps<T>) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? options.filter((o) => o.label.toLowerCase().includes(q))
      : options;
  }, [options, query]);

  const allSelected =
    visible.length > 0 && visible.every((o) => selected.has(o.value));
  const title =
    selected.size === 0
      ? label
      : selected.size === 1
        ? (options.find((o) => selected.has(o.value))?.label ?? label)
        : `${selected.size} selected`;

  return (
    <Dropdown
      title={title}
      isOpen={isOpen}
      onToggle={onToggle}
      variant="analytics"
      size={Size.SM}
      showActive={selected.size > 0}
      menuClassName="max-h-[320px] overflow-y-auto"
      buttonClassName={fullWidth ? "w-full" : ""}
    >
      <div className="flex flex-col gap-1 min-w-[220px] font-mulish">
        {searchPlaceholder && (
          <div className="pb-2">
            <Search
              placeholder={searchPlaceholder}
              value={query}
              onChange={setQuery}
              showClearButton={false}
              inputClassName="text-xs"
              height="40px"
              variant={SearchVariant.OUTLINE}
              size={Size.SM}
            />
          </div>
        )}
        <label className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-[#F4FAF6] cursor-pointer">
          <Checkbox
            checked={allSelected}
            onChange={() => {
              const values = visible.map((o) => o.value);
              allSelected ? onClear(values) : onSelectAll(values);
            }}
          >
            <span
              className={`text-sm ${
                selected.size === 0
                  ? "font-bold text-[#1C9C6D]"
                  : "text-[#252525]"
              }`}
            >
              Select all
            </span>
          </Checkbox>
        </label>
        {visible.map((o) => (
          <label
            key={String(o.value)}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-[#F4FAF6] cursor-pointer"
          >
            <Checkbox
              checked={selected.has(o.value)}
              onChange={() => onToggleValue(o.value)}
            >
              <span className="text-sm text-[#252525]">{o.label}</span>
            </Checkbox>
          </label>
        ))}
      </div>
    </Dropdown>
  );
}

export default MultiSelectDropdown;
